import { defineStore } from 'pinia'
import propertyApi from '@/api/property'
import type { CancelablePromiseWrapper } from '@/utils'
import type {
  MarketSearchFilters,
  MarketComparablePayload,
  MarketStatsQuery,
  PropertyDetailsResponse,
  PropertySearchResponse,
  PropertyReportPropertyInsert,
  SimpleProperty,
  Viewport
} from 'common'

export const usePropertyStore = defineStore('property', {
  state: () => ({
    propertySearchCancelable: null as CancelablePromiseWrapper<PropertySearchResponse[]> | null
  }),
  actions: {
    propertySearch(
      q: string,
      filters: {
        lat?: number
        lon?: number
        zipCode?: string
        state?: string
        maxDistance?: number
      },
      cancelPrevious = false
    ) {
      if (cancelPrevious && this.propertySearchCancelable) {
        this.propertySearchCancelable.cancel()
        this.propertySearchCancelable = null
      }

      const promise = propertyApi
        .propertySearch(q, {
          lat: filters.lat,
          lon: filters.lon,
          state: filters.state,
          zipCode: filters.zipCode,
          maxDistance: filters.maxDistance
        })
        .then((results) => {
          this.propertySearchCancelable = null
          for (const result of results) {
            // We remove created_on because we don't want to use this anywhere.
            // This field was created for our API customers only.
            delete result.created_on
          }
          return results
        })

      this.propertySearchCancelable = promise
      return promise.unwrap()
    },
    async details(id: string) {
      return propertyApi.details(id)
    },
    async insertProperty(data: PropertyReportPropertyInsert) {
      return propertyApi.insertProperty(data)
    },
    async comparables(
      subject: PropertyDetailsResponse | SimpleProperty,
      params: {
        excludedIds: string[]
        selectedIds: string[]
        topN: number
        renovations?: number
        maxDistance?: number
        minNumberUnits?: number
        maxNumberUnits?: number
        minYearBuilt?: number
        maxYearBuilt?: number
        minNumberStories?: number
        maxNumberStories?: number
        useEmbedding?: boolean
        isAffordable?: boolean
        isSenior?: boolean
        isStudent?: boolean
        maxMilesToUniversity?: number
        maxMilesToHospital?: number
      }
    ) {
      return propertyApi.comparables(subject, params)
    },
    async marketComparables(filters: MarketComparablePayload) {
      return propertyApi.marketComparables(filters)
    },
    async marketStatistics(subject: MarketStatsQuery) {
      return propertyApi.marketStatistics(subject)
    },
    async marketShapeSearch(q: string) {
      return propertyApi.marketShapeSearch(q)
    },
    async shapeSearchByCentroid(
      tag: 'MSA' | 'City' | 'County' | 'Zip Code' | 'Neighborhood',
      lat: number,
      lon: number
    ) {
      return propertyApi.shapeSearchByCentroid(tag, lat, lon)
    },
    async marketDemographics(filters: MarketSearchFilters) {
      return propertyApi.marketDemographics(filters)
    }
  }
})
